import { ComponentProps, useState } from "react";
import { useInView } from "react-intersection-observer";
import { GetArticleZhHansPayload } from "@backend/utils/constants";
import Card from "@particles/card/Card";
import { getTemplateGProps } from "@particles/card/templates/TemplateG/utils/helpers";
import { SanitizedFeedSectionArticle } from "@typings/Cue";
import { tryCatch } from "@typings/TryCatch";
import { getEnvironment } from "@util/constant";
import { getEnvironmentDomain } from "@util/helpers";
import axios from "axios";

type UseLoadMoreArticlesTranslatedProps = {
  initialList: ComponentProps<typeof Card>[];
  size: number;
};

/** Hook to be used to fetch subsequent translated articles on the zh-hans page. */
export const useLoadMoreArticlesTranslated = ({
  initialList,
  size,
}: UseLoadMoreArticlesTranslatedProps) => {
  const [hasMoreStories, setHasMoreStories] = useState<boolean>(true);
  const [loadMoreData, setLoadMoreData] = useState<
    ComponentProps<typeof Card>[]
  >([]);

  const handleLoadMore = async (initialList: ComponentProps<typeof Card>[]) => {
    const page = [...initialList, ...loadMoreData].length / size + 1;

    const data = await fetchTranslatedStories({
      page,
      size,
      platform: "web",
      lang: "zh-hans",
    });

    if (typeof data === "undefined") {
      setHasMoreStories(false);
      return;
    }

    const envDomain = getEnvironmentDomain(getEnvironment());
    const articles = data.map((article): SanitizedFeedSectionArticle => {
      return {
        ...article,
        urlPath: article.urlPath.replace(envDomain, `${envDomain}/zh-hans`),
      };
    });
    const articleProps = getTemplateGProps(articles);
    setLoadMoreData([...loadMoreData, ...articleProps]);

    if (articleProps.length < size) {
      setHasMoreStories(false);
    }
  };

  const { ref } = useInView({
    threshold: 0,
    onChange: (inView) => {
      if (!inView) return;
      if (!hasMoreStories) return;
      if (initialList.length < size) return;

      handleLoadMore(initialList);
    },
  });

  return { ref, loadMoreData };
};

/** Fetch translated stories via user function api. */
const fetchTranslatedStories = async (
  payload: GetArticleZhHansPayload
): Promise<SanitizedFeedSectionArticle[] | undefined> => {
  const request = axios.get<SanitizedFeedSectionArticle[]>(
    "/_plat/api/v1/articles/translated",
    { params: payload }
  );
  const { data, error } = await tryCatch(request);

  if (error) {
    console.error(error); // eslint-disable-line no-console
    return;
  }

  return data.data;
};
